/* eslint-disable camelcase */
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Autocomplete from '@material-ui/lab/Autocomplete'
import FacilityAlgoliaSettingsField from 'components/facilities/facility-algolia-settings-field'
import TipWell from 'components/tip-well'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import { IconTrashcan } from 'components/atoms/icons'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useSupportContactActions } from 'lib/support'
import usStates from 'utils/usStates'
import useStyles, {
  ErrorMsg,
  FacilityImage,
  HelperMsg,
  SectionTitle,
} from 'components/coaches-profile/profile-stepper.styles'
import TextFieldCounter from 'components/text-field-counter'
import { CLOUDINARY_CLOUDNAME, CLOUDINARY_UPLOAD_PRESET_LARGE } from 'env'
import { handleImageUpload } from 'lib/cloudinary/uploadImage'
import {
  getFieldProps,
  requiredLabel,
  useCreateProfileFieldStyles,
} from '../../create-profile-form/field-styles'

const photosMsg = 'Add one photo per facility.'

const uploadOptions = {
  cloudName: CLOUDINARY_CLOUDNAME,
  upload_preset: CLOUDINARY_UPLOAD_PRESET_LARGE,
  sources: ['local', 'camera'],
  cropping: true,
  croppingAspectRatio: 1.7,
  showSkipCropButton: false,
  resourceType: 'image',
  multiple: false,
  folder: 'dot-com-images',
  showPoweredBy: false,
}

const CoachProfileEditFacilities = props => {
  const {
    formPrefix,
    isPrimary,
    deleteMe,
    errors,
    setFieldValue,
    register,
    control,
    watch,
    facilityIndex,
    facility,
  } = props

  const { composeSupportMessage } = useSupportContactActions()
  const { getValues } = useFormContext()
  const allValues = getValues()
  const facilityValuesLocal =
    (allValues?.facilities && allValues.facilities[facilityIndex]) || {}

  const facilityValues =
    (!isEmpty(facilityValuesLocal) && facilityValuesLocal) || facility

  const styles = useStyles()
  const fieldClasses = useCreateProfileFieldStyles()
  const fieldProps = getFieldProps(fieldClasses)
  const formField = name => `${formPrefix}.${name}`
  const pgaFacilityId = watch(formField('pgaFacilityId'))
  const isPgaFacilitySelected = Boolean(pgaFacilityId)
  watch([formField('isPrivate')])

  const renderCheckboxControl = checkboxProps => (
    <Checkbox
      color="default"
      size="small"
      classes={{
        root: fieldClasses.selectionCheckboxRoot,
        checked: fieldClasses.selectionCheckboxChecked,
      }}
      {...checkboxProps}
    />
  )

  const [uploadError, setUploadError] = useState()

  const setPGAFacility = hit => {
    const {
      name: facilityName,
      address1: street,
      city,
      state,
      zip: zipCode,
      phone: phoneNumber,
      website,
      operations_type_label,
      facility_id: pgaFacilityId,
    } = hit

    setFieldValue(`${formPrefix}`, {
      ...facilityValues,
      facilityName,
      street,
      city,
      state,
      zipCode,
      phoneNumber,
      website:
        website && !/^https?:\/\//i.test(website)
          ? `http://${website}`
          : website,
      pgaFacilityId,
      isPrivate:
        operations_type_label === 'Private Equity' ||
        operations_type_label === 'Private Non-Equity',
      customData: {
        ...facilityValues.customData,
        offersCoachingAtPrivateClub: false,
      },
    })
  }

  const clearPGAFacilitySelection = () => {
    setFieldValue(`${formPrefix}`, {
      ...facilityValues,
      facilityId: null,
      facilityName: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      phoneNumber: '',
      website: '',
      pgaFacilityId: null,
      isPrivate: false,
      customData: {
        ...facilityValues.customData,
        offersCoachingAtPrivateClub: false,
      },
    })
  }

  const facilityFields = (
    <Box
      key={pgaFacilityId || 'custom'}
      className={fieldClasses.fieldStack}
      width="100%"
    >
      {isPgaFacilitySelected ? (
        <TipWell
          icon={InfoOutlinedIcon}
          caption={
            <>
              You&apos;ve selected a pre-existing facility. Editing pre-existing
              facility information is disabled. If the data appears incorrect,
              please{' '}
              <Box
                component="button"
                type="button"
                onClick={() =>
                  composeSupportMessage(
                    `I found an issue with ${facilityValues.facilityName}'s data.  Specifically....`,
                  )
                }
                style={{
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  background: 'none',
                  color: 'inherit',
                  font: 'inherit',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  whiteSpace: 'nowrap',
                  display: 'inline',
                }}
              >
                contact us
              </Box>{' '}
              to let us know.
            </>
          }
        />
      ) : null}
      <Controller
        name={formField('facilityName')}
        control={control}
        render={({ onChange, onBlur, value }, _) => (
          <FacilityAlgoliaSettingsField
            name={formField('facilityName')}
            label={requiredLabel('Facility Name')}
            inputClassName={fieldClasses.fieldInput}
            onSelect={setPGAFacility}
            onClear={clearPGAFacilitySelection}
            value={value}
            onBlur={onBlur}
            error={!!errors.facilityName}
            onChange={onChange}
            externalFacilityId={pgaFacilityId}
            city={facilityValues.city}
            state={facilityValues.state}
          />
        )}
      />
      <TextField
        {...fieldProps}
        name={formField('street')}
        label={requiredLabel('Address')}
        type="text"
        defaultValue={facilityValues.street || ''}
        inputRef={register()}
        error={!!errors.street}
        disabled={isPgaFacilitySelected}
      />
      <Box className={fieldClasses.locationRow}>
        <TextField
          {...fieldProps}
          name={formField('city')}
          label={requiredLabel('City')}
          type="text"
          defaultValue={facilityValues.city || ''}
          inputRef={register()}
          error={!!errors?.city}
          disabled={isPgaFacilitySelected}
        />
        <Controller
          name={formField('state')}
          control={control}
          render={({ value, onChange }) => (
            <Autocomplete
              name={formField('state')}
              disableClearable
              disabled={isPgaFacilitySelected}
              value={value || ''}
              options={usStates}
              onChange={(e, nextValue) => onChange(nextValue)}
              className={fieldClasses.selectInput}
              renderInput={params => (
                <TextField
                  {...params}
                  {...fieldProps}
                  label={requiredLabel('State')}
                  error={!!errors.state}
                />
              )}
            />
          )}
        />
        <TextField
          {...fieldProps}
          name={formField('zipCode')}
          label={requiredLabel('Zip')}
          type="text"
          defaultValue={facilityValues.zipCode || ''}
          inputRef={register()}
          error={!!errors.zipCode}
          disabled={isPgaFacilitySelected}
        />
      </Box>
      <TextField
        {...fieldProps}
        name={formField('phoneNumber')}
        label="Phone"
        type="text"
        defaultValue={facilityValues.phoneNumber || ''}
        inputRef={register()}
        error={!!errors.phoneNumber}
        disabled={isPgaFacilitySelected}
      />
      <TextField
        {...fieldProps}
        name={formField('website')}
        label="Facility Website"
        type="text"
        defaultValue={facilityValues.website || ''}
        inputRef={register()}
        error={!!errors?.website}
        disabled={isPgaFacilitySelected}
      />
      <Box className={fieldClasses.selectionCheckboxList}>
        <Controller
          control={control}
          name={formField('isPrivate')}
          render={({ onChange, onBlur, value, name, ref }) => (
            <FormControlLabel
              control={renderCheckboxControl({
                name,
                onBlur,
                inputRef: ref,
                checked: Boolean(value),
                onChange: e => onChange(e.target.checked),
                disabled: isPgaFacilitySelected,
              })}
              label="This is a private facility"
              className={fieldClasses.selectionCheckboxField}
              classes={{ label: fieldClasses.selectionCheckboxLabel }}
              style={{ userSelect: 'none' }}
            />
          )}
        />
        {facilityValues.isPrivate ? (
          <Controller
            control={control}
            name={formField('customData.offersCoachingAtPrivateClub')}
            render={({ onChange, onBlur, value, name, ref }) => (
              <FormControlLabel
                control={renderCheckboxControl({
                  id: `checkbox-public-coaching-${facilityValues.facilityRank}`,
                  name,
                  onBlur,
                  checked: Boolean(value),
                  inputRef: ref,
                  onChange: e => onChange(e.target.checked),
                  disabled: isPgaFacilitySelected,
                })}
                label="I offer coaching to the public at this facility"
                htmlFor={`checkbox-public-coaching-${facilityValues.facilityRank}`}
                className={fieldClasses.selectionCheckboxField}
                classes={{ label: fieldClasses.selectionCheckboxLabel }}
                style={{ userSelect: 'none' }}
              />
            )}
          />
        ) : null}
      </Box>
    </Box>
  )

  return (
    <Box width="100%">
      <Box display="none" aria-hidden="true">
        <input
          name={formField('pgaFacilityId')}
          type="hidden"
          ref={register()}
          value={facilityValues.pgaFacilityId || ''}
        />
        <input
          name={formField('customData.facilityRank')}
          type="hidden"
          ref={register()}
          value={facilityIndex + 1}
        />
      </Box>
      {!isPrimary ? (
        <Box className={fieldClasses.selectionCard}>
          <Box className={fieldClasses.selectionCardHeader}>
            <Box
              className={fieldClasses.selectionCardHeaderRow}
              style={{ justifyContent: 'space-between', width: '100%' }}
            >
              <Typography
                component="h4"
                className={fieldClasses.selectionCardTitle}
              >
                Additional facility
              </Typography>
              <Button
                className={fieldClasses.selectionCardHeaderAction}
                type="button"
                startIcon={<DeleteOutlinedIcon fontSize="small" />}
                onClick={deleteMe}
              >
                Delete
              </Button>
            </Box>
          </Box>
          <Box className={fieldClasses.selectionCardBody}>{facilityFields}</Box>
        </Box>
      ) : (
        facilityFields
      )}
      <Box display="none">
        <SectionTitle>Description</SectionTitle>
        <HelperMsg>Add your own description for this facility.</HelperMsg>
        <Controller
          name={formField('customData.description')}
          control={control}
          render={({ onChange, onBlur, value }, _) => (
            <TextFieldCounter
              characterLimit={1000}
              label="Facility Description"
              multiline
              rows={4}
              name={formField('customData.description')}
              onChange={onChange}
              onBlur={onBlur}
              type="text"
              variant="outlined"
              fullWidth
              value={value}
              style={{ marginTop: '1rem' }}
              className={`${fieldClasses.fieldInput} multiline ${
                errors.customData && errors.customData.description
                  ? ' error'
                  : ''
              }`}
            />
          )}
        />
        {/* Photos Sub-Section */}
        <SectionTitle style={{ marginTop: '1rem' }}>Photos</SectionTitle>
        <HelperMsg>{photosMsg}</HelperMsg>
        <Controller
          name={formField('customData.photos[0]')}
          control={control}
          render={({ onChange, value }, _) => (
            <>
              {value ? (
                <FacilityImage>
                  <img src={value} alt="Facility" />
                </FacilityImage>
              ) : null}
              <Box className={styles.flexRow} style={{ margin: '1rem 0 2rem' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  type="button"
                  onClick={() =>
                    handleImageUpload(
                      uploadOptions,
                      setFieldValue,
                      setUploadError,
                      `${formField('customData')}.photos`,
                      [],
                    )
                  }
                >
                  Upload Photo
                </Button>
                <ErrorMsg>{uploadError}</ErrorMsg>
                {value ? (
                  <Button
                    className={fieldClasses.repeaterDeleteButtonFilled}
                    variant="contained"
                    color="default"
                    disableElevation
                    size="large"
                    type="button"
                    startIcon={<IconTrashcan />}
                    onClick={() => {
                      setFieldValue(formField('customData.photos[0]'), null)
                    }}
                  >
                    Delete
                  </Button>
                ) : null}
              </Box>
            </>
          )}
        />
      </Box>
    </Box>
  )
}

CoachProfileEditFacilities.propTypes = {
  formPrefix: PropTypes.string,
  isPrimary: PropTypes.bool,
  errors: PropTypes.object,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  facilityIndex: PropTypes.number,
}

CoachProfileEditFacilities.defaultProps = {
  errors: {},
}

export default CoachProfileEditFacilities
